import React, { useEffect, useRef, useState } from "react";
import { LogOut, User, Edit2 } from "react-feather";
import { useNavigate } from "react-router-dom";
import { Customer } from "../../contexts/CustomerContext";
import { fireConfetti } from "../../utils/confetti";
import styles from "./AvatarMenu.module.scss";

interface AvatarMenuProps {
  customer: Customer | null;
  onSignOut: () => void;
}

const AvatarMenu: React.FC<AvatarMenuProps> = ({ customer, onSignOut }) => {
  const [open, setOpen] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const tapCount = useRef(0);
  const tapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleAvatarClick = () => {
    // Menu always responds immediately
    setOpen((v) => !v);

    // Rapid-tap counter runs silently in the background
    if (tapTimer.current) clearTimeout(tapTimer.current);
    tapCount.current += 1;
    if (tapCount.current >= 5) {
      tapCount.current = 0;
      setOpen(false);
      fireConfetti();
      return;
    }
    tapTimer.current = setTimeout(() => {
      tapCount.current = 0;
    }, 400);
  };

  const initials = customer
    ? `${customer.firstName[0] ?? ""}${customer.lastName[0] ?? ""}`.toUpperCase()
    : "";

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className={styles.Wrap} ref={ref}>
      <button
        className={styles.Btn}
        onClick={handleAvatarClick}
        aria-label="Account menu"
        type="button"
      >
        {initials || <User size={16} />}
      </button>

      <div className={`${styles.Dropdown} ${open ? styles.DropdownOpen : ""}`}>
        {customer && (
          <>
            <div className={styles.Header}>
              <div className={styles.HeaderAvatar}>{initials}</div>
              <div className={styles.HeaderInfo}>
                <span className={styles.HeaderName}>
                  {customer.firstName} {customer.lastName}
                </span>
                {customer.email && (
                  <span className={styles.HeaderEmail}>{customer.email}</span>
                )}
              </div>
            </div>
            <div className={styles.Divider} />
          </>
        )}

        <button
          className={styles.MenuItem}
          onClick={() => {
            setOpen(false);
            navigate("/profile/edit");
          }}
          type="button"
        >
          <Edit2 size={15} />
          Edit profile
        </button>

        <button
          className={styles.SignOutBtn}
          onClick={() => {
            setOpen(false);
            setConfirmSignOut(true);
          }}
          type="button"
        >
          <LogOut size={15} />
          Sign out
        </button>
      </div>

      <div
        className={`${styles.Overlay} ${confirmSignOut ? styles.OverlayActive : ""}`}
        onClick={() => setConfirmSignOut(false)}
      >
        <div
          className={`${styles.ConfirmCard} ${confirmSignOut ? styles.ConfirmCardActive : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.ConfirmIconWrap}>
            <LogOut size={28} />
          </div>
          <h3 className={styles.ConfirmTitle}>Sign out?</h3>
          <p className={styles.ConfirmBody}>
            Are you sure you want to sign out of your account?
          </p>
          <div className={styles.ConfirmActions}>
            <button
              className={styles.BtnDanger}
              type="button"
              onClick={() => {
                setConfirmSignOut(false);
                onSignOut();
              }}
            >
              Sign out
            </button>
            <button
              className={styles.BtnCancel}
              type="button"
              onClick={() => setConfirmSignOut(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvatarMenu;
